import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group, Select, Autocomplete } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { CreateBusinessCommand, CreatePlaceCommand } from '@/domain/entities/Business';
import { useCreateBusinessMutation } from '@/application/queries/businessQueries';
import { useCompaniesByRegion } from '@/application/queries/useCompanies';
import { useAuth } from '../../../hooks/useAuth';
import { useRegionsQuery } from '@/application/queries/regionQueries';

/** Known category values from the OSM import pipeline */
const CATEGORY_OPTIONS = [
  { value: 'retail', label: 'Retail' },
  { value: 'office', label: 'Office' },
  { value: 'craft', label: 'Craft' },
  { value: 'food_service', label: 'Food Service' },
  { value: 'financial', label: 'Financial' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'service', label: 'Service' },
  { value: 'tourism', label: 'Tourism' },
  { value: 'leisure', label: 'Leisure' },
  { value: 'general', label: 'General' }
];

/** Known business types from OSM, grouped by prefix. Format: "group:subtype" */
const BUSINESS_TYPE_OPTIONS: string[] = [
  // amenity
  'amenity:restaurant', 'amenity:cafe', 'amenity:fast_food', 'amenity:bar', 'amenity:pub',
  'amenity:biergarten', 'amenity:food_court', 'amenity:ice_cream',
  'amenity:bank', 'amenity:bureau_de_change',
  'amenity:pharmacy', 'amenity:clinic', 'amenity:dentist', 'amenity:doctors', 'amenity:veterinary',
  'amenity:car_rental', 'amenity:car_wash', 'amenity:fuel', 'amenity:charging_station',
  'amenity:cinema', 'amenity:theatre', 'amenity:nightclub', 'amenity:casino',
  'amenity:events_venue', 'amenity:music_venue',
  'amenity:driving_school', 'amenity:marketplace', 'amenity:arts_centre',
  'amenity:studio', 'amenity:internet_cafe',
  // tourism
  'tourism:hotel', 'tourism:motel', 'tourism:hostel', 'tourism:guest_house',
  'tourism:apartment', 'tourism:chalet', 'tourism:camp_site',
  'tourism:theme_park', 'tourism:zoo', 'tourism:aquarium',
  // leisure
  'leisure:sports_centre', 'leisure:fitness_centre', 'leisure:golf_course',
  'leisure:miniature_golf', 'leisure:bowling_alley', 'leisure:ice_rink',
  'leisure:swimming_pool', 'leisure:marina', 'leisure:amusement_arcade',
  'leisure:escape_game', 'leisure:water_park', 'leisure:beach_resort',
  'leisure:trampoline_park', 'leisure:spa', 'leisure:dance',
  'leisure:horse_riding', 'leisure:sauna', 'leisure:tanning_salon'
];

export interface CreateBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * CreateBusinessModal - Modal for creating a new business
 *
 * Features:
 * - Form with required fields (name, category, region)
 * - Category dropdown with known OSM values
 * - BusinessType searchable autocomplete with known OSM types (allows custom values)
 * - Address information (street, city, state, zip)
 * - Optional fields (description, phone, website, email)
 * - Form validation
 * - Success/error notifications via mutation hook
 * - Loading state during save
 */
export const CreateBusinessModal: React.FC<CreateBusinessModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const createBusinessMutation = useCreateBusinessMutation();

  // Fetch regions for dropdown
  const { data: regionsData } = useRegionsQuery(
    { pageNumber: 1, pageSize: 100 },
    user?.role,
    isOpen // Only fetch when modal is open
  );

  const regions = regionsData?.items ?? [];

  // Form state (must be declared before hooks that reference formData)
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    regionId: '',
    companyId: '',
    businessType: '',
    description: '',
    phone: '',
    website: '',
    email: '',
    openingHours: '',
    // Address fields
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA'
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch companies for selected region
  const { data: companiesData } = useCompaniesByRegion(
    formData.regionId,
    { page: 1, pageSize: 200 },
    { enabled: isOpen && formData.regionId !== '' }
  );

  // Build company options for the Select dropdown
  const companyOptions = useMemo(() => {
    if (!companiesData?.companies) return [];
    return companiesData.companies
      .filter((c) => c.isActive)
      .map((c) => ({
        value: c.id,
        label: `${c.name} (${c.businessCount} location${c.businessCount !== 1 ? 's' : ''})`
      }));
  }, [companiesData]);

  /**
   * Reset form when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        category: '',
        regionId: '',
        companyId: '',
        businessType: '',
        description: '',
        phone: '',
        website: '',
        email: '',
        openingHours: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'USA'
      });
      setErrors({});
    }
  }, [isOpen]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback(
    (field: string, value: string): void => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };
        // Reset companyId when region changes
        if (field === 'regionId') {
          updated.companyId = '';
        }
        return updated;
      });
      // Clear error for this field
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [errors]
  );

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Business name is required
    if (!formData['name'] || formData['name'].trim() === '') {
      newErrors['name'] = 'Business name is required';
    } else if (formData['name'].length > 200) {
      newErrors['name'] = 'Business name must be 200 characters or less';
    }

    // Category is required
    if (!formData['category'] || formData['category'].trim() === '') {
      newErrors['category'] = 'Category is required';
    }

    // Region is required
    if (!formData['regionId'] || formData['regionId'].trim() === '') {
      newErrors['regionId'] = 'Region is required';
    }

    // Street address is required for Place
    if (!formData['street'] || formData['street'].trim() === '') {
      newErrors['street'] = 'Street address is required';
    }

    // City is required for Place
    if (!formData['city'] || formData['city'].trim() === '') {
      newErrors['city'] = 'City is required';
    }

    // Email validation (if provided)
    if (formData['email'] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData['email'])) {
      newErrors['email'] = 'Invalid email format';
    }

    // Website validation (if provided)
    if (formData['website'] && !/^https?:\/\/.+/.test(formData['website'])) {
      newErrors['website'] = 'Website must start with http:// or https://';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validate()) {
      return;
    }

    try {
      // Build CreatePlaceCommand
      const placeData: CreatePlaceCommand = {
        name: formData['name'], // Use business name for place
        placeType: 'business_location',
        addrStreet: formData['street'] || undefined,
        addrCity: formData['city'] || undefined,
        addrState: formData['state'] || undefined,
        addrPostcode: formData['postalCode'] || undefined,
        addrCountry: formData['country'] || undefined
      };

      // Build CreateBusinessCommand
      const command: CreateBusinessCommand = {
        name: formData['name'],
        category: formData['category'],
        regionId: formData['regionId'],
        placeData,
        companyId: formData['companyId'] || undefined,
        businessType: formData['businessType'] || undefined,
        description: formData['description'] || undefined,
        phone: formData['phone'] || undefined,
        website: formData['website'] || undefined,
        email: formData['email'] || undefined,
        openingHours: formData['openingHours'] || undefined
      };

      await createBusinessMutation.mutateAsync({
        command,
        userRole: user?.role
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by mutation hook (notifications)
      console.error('Create business failed:', error);
    }
  }, [formData, validate, createBusinessMutation, user, onClose, onSuccess]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  // Prepare region options for select
  const regionOptions = regions.map((region) => ({
    value: region.id,
    label: region.name
  }));

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Create New Business"
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Business Name (Required) */}
        <TextInput
          label="Business Name"
          placeholder="Enter business name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.currentTarget.value)}
          error={errors['name']}
          required
          withAsterisk
        />

        {/* Region (Required) */}
        <Select
          label="Region"
          placeholder="Select region"
          value={formData.regionId}
          onChange={(value) => handleChange('regionId', value ?? '')}
          data={regionOptions}
          error={errors['regionId']}
          required
          withAsterisk
          searchable
        />

        {/* Company (Optional) */}
        <Select
          label="Company (Optional)"
          placeholder={formData.regionId ? 'Select existing company or leave empty' : 'Select a region first'}
          value={formData.companyId || null}
          onChange={(value) => handleChange('companyId', value ?? '')}
          data={companyOptions}
          disabled={!formData.regionId}
          searchable
          clearable
        />

        {/* Category (Required) */}
        <Select
          label="Category"
          placeholder="Select category"
          value={formData.category}
          onChange={(value) => handleChange('category', value ?? '')}
          data={CATEGORY_OPTIONS}
          error={errors['category']}
          required
          withAsterisk
          searchable
        />

        {/* Business Type (Optional - searchable with known OSM types, allows custom input) */}
        <Autocomplete
          label="Business Type"
          placeholder="Search or type custom (e.g., amenity:restaurant, shop:bakery)"
          value={formData.businessType}
          onChange={(value) => handleChange('businessType', value)}
          data={BUSINESS_TYPE_OPTIONS}
          limit={15}
        />

        {/* Description (Optional) */}
        <Textarea
          label="Description"
          placeholder="Describe the business"
          value={formData.description}
          onChange={(e) => handleChange('description', e.currentTarget.value)}
          error={errors['description']}
          rows={3}
          maxLength={500}
        />

        {/* Address Section */}
        <TextInput
          label="Street Address"
          placeholder="123 Main St"
          value={formData.street}
          onChange={(e) => handleChange('street', e.currentTarget.value)}
          error={errors['street']}
          required
          withAsterisk
        />

        <Group grow>
          <TextInput
            label="City"
            placeholder="Seattle"
            value={formData.city}
            onChange={(e) => handleChange('city', e.currentTarget.value)}
            error={errors['city']}
            required
            withAsterisk
          />

          <TextInput
            label="State"
            placeholder="WA"
            value={formData.state}
            onChange={(e) => handleChange('state', e.currentTarget.value)}
          />

          <TextInput
            label="Zip Code"
            placeholder="98101"
            value={formData.postalCode}
            onChange={(e) => handleChange('postalCode', e.currentTarget.value)}
          />
        </Group>

        {/* Contact Information */}
        <TextInput
          label="Phone"
          placeholder="(555) 123-4567"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.currentTarget.value)}
        />

        <TextInput
          label="Website"
          placeholder="https://example.com"
          value={formData.website}
          onChange={(e) => handleChange('website', e.currentTarget.value)}
          error={errors['website']}
        />

        <TextInput
          label="Email"
          placeholder="contact@business.com"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.currentTarget.value)}
          error={errors['email']}
        />

        {/* Opening Hours */}
        <Textarea
          label="Opening Hours"
          placeholder="Mon-Fri: 9am-5pm"
          value={formData.openingHours}
          onChange={(e) => handleChange('openingHours', e.currentTarget.value)}
          rows={2}
        />

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={createBusinessMutation.isPending}
            disabled={createBusinessMutation.isPending}
          >
            Create Business
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
